import { 
  collection, 
  query, 
  where, 
  orderBy,
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDoc,
  setDoc,
  limit,
  serverTimestamp 
} from 'firebase/firestore'
import { db, auth } from '../firebase'
import { countRelevantChords, isExerciseSuitableForNode } from '../utils/nodeCriteria'
import { getUnlockedCadenceValues } from '../data/parcoursTree'
import { sanitizeAutoTags } from '../data/knownTags'
import { getDifficultyFilterValues, getDisplayDifficulty } from '../utils/difficultyFromContent'
import {
  getAllowedDifficultiesForNode
} from '../utils/parcoursDifficultySequence'
import { pickParcoursRandomPool } from '../utils/parcoursExerciseSelection'
import { computeAutoTagsForStoredExercise as computeAutoTagsForStoredExerciseImpl } from '../utils/computeAutoTagsForStoredExercise'
import { getComposerDisplayName, getComposerFamilyName } from '../utils/exerciseDisplay'
import { composerToSlug } from './worksReference'

let _composerCounts = null
let _composerCountsTimestamp = 0
const COMPOSER_COUNTS_TTL = 10 * 60 * 1000

function updateComposerCountsCache(exercises) {
  const counts = new Map()
  for (const ex of exercises) {
    const family = getComposerFamilyName(ex.metadata) || getComposerDisplayName(ex.metadata)
    if (family) {
      const slug = composerToSlug(family)
      if (slug) counts.set(slug, (counts.get(slug) || 0) + 1)
    }
  }
  _composerCounts = counts
  _composerCountsTimestamp = Date.now()
}

export function getCachedComposerExerciseCounts() {
  if (_composerCounts && (Date.now() - _composerCountsTimestamp) < COMPOSER_COUNTS_TTL) {
    return _composerCounts
  }
  return null
}

function cleanExerciseTags(exercise) {
  if (exercise && Array.isArray(exercise.autoTags)) {
    exercise.autoTags = sanitizeAutoTags(exercise.autoTags)
  }
  return exercise
}

/** Remplace récursivement undefined par null (Firestore n'accepte pas undefined). */
function sanitizeForFirestore(value) {
  if (value === undefined) return null
  if (value === null) return null
  if (value && typeof value === 'object' && typeof value.toDate === 'function') return value
  if (value instanceof Date) return value
  if (Array.isArray(value)) return value.map(sanitizeForFirestore)
  if (typeof value === 'object' && value !== null) {
    const out = {}
    for (const key of Object.keys(value)) {
      out[key] = sanitizeForFirestore(value[key])
    }
    return out
  }
  return value
}

function normalizeShareCode(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
}

function generateShareCode(length = 6) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let code = ''
  for (let i = 0; i < length; i += 1) {
    code += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return code
}

/**
 * Récupère tous les exercices d'un auteur
 */
export async function getExercisesByAuthor(authorId) {
  try {
    const q = query(
      collection(db, 'exercises'),
      where('authorId', '==', authorId)
    )
    const querySnapshot = await getDocs(q)
    const exercises = []
    querySnapshot.forEach((doc) => {
      exercises.push(cleanExerciseTags({
        id: doc.id,
        ...doc.data()
      }))
    })
    exercises.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(0)
      const dateB = b.createdAt?.toDate?.() || new Date(0)
      return dateB - dateA
    })
    updateComposerCountsCache(exercises)
    return exercises
  } catch (error) {
    console.error('Erreur lors de la récupération des exercices:', error)
    throw error
  }
}

/**
 * Récupère tous les exercices (pour les profs : vue "Tous les exercices")
 */
export async function getAllExercisesForTeachers() {
  try {
    const q = query(collection(db, 'exercises'))
    const querySnapshot = await getDocs(q)
    const exercises = []
    querySnapshot.forEach((docSnap) => {
      exercises.push(cleanExerciseTags({
        id: docSnap.id,
        ...docSnap.data()
      }))
    })
    exercises.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(0)
      const dateB = b.createdAt?.toDate?.() || new Date(0)
      return dateB - dateA
    })
    updateComposerCountsCache(exercises)
    return exercises
  } catch (error) {
    console.error('Erreur lors de la récupération des exercices:', error)
    throw error
  }
}

/**
 * Récupère un exercice par son ID
 */
export async function getExerciseById(exerciseId) {
  try {
    const docRef = doc(db, 'exercises', exerciseId)
    const docSnap = await getDoc(docRef)
    if (docSnap.exists()) {
      return cleanExerciseTags({
        id: docSnap.id,
        ...docSnap.data()
      })
    } else {
      return null
    }
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'exercice:', error)
    throw error
  }
}

/**
 * Récupère un exercice via un code court partageable.
 */
export async function getExerciseByShareCode(code) {
  const normalized = normalizeShareCode(code)
  if (!normalized) return null
  const codeRef = doc(db, 'exerciseCodes', normalized)
  const codeSnap = await getDoc(codeRef)
  if (!codeSnap.exists()) return null
  const exerciseId = codeSnap.data()?.exerciseId
  if (!exerciseId) return null
  return getExerciseById(exerciseId)
}

/**
 * Retourne un code court existant ou en crée un nouveau pour un exercice.
 */
export async function getOrCreateExerciseShareCode(exerciseId) {
  const normalizedExerciseId = String(exerciseId || '').trim()
  if (!normalizedExerciseId) throw new Error('exerciseId requis')

  const existingQ = query(
    collection(db, 'exerciseCodes'),
    where('exerciseId', '==', normalizedExerciseId),
    limit(1)
  )
  const existingSnapshot = await getDocs(existingQ)
  if (!existingSnapshot.empty) {
    return { code: existingSnapshot.docs[0].id, exerciseId: normalizedExerciseId }
  }

  let attempts = 0
  while (attempts < 8) {
    attempts += 1
    const code = generateShareCode(6)
    const codeRef = doc(db, 'exerciseCodes', code)
    const collision = await getDoc(codeRef)
    if (collision.exists()) continue
    await setDoc(codeRef, {
      exerciseId: normalizedExerciseId,
      createdAt: serverTimestamp()
    })
    return { code, exerciseId: normalizedExerciseId }
  }

  throw new Error('Impossible de générer un code partageable pour cet exercice.')
}

/**
 * Résout une référence exercice (ID direct ou code court) vers l'exercice.
 */
export async function resolveExerciseReference(reference) {
  const value = String(reference || '').trim()
  if (!value) return null
  const byId = await getExerciseById(value)
  if (byId) return byId
  return getExerciseByShareCode(value)
}

/**
 * Recherche des exercices publics avec filtres
 */
export async function searchPublicExercises(filters = {}) {
  try {
    let q = query(
      collection(db, 'exercises'),
      where('status', '==', 'published')
    )
    if (filters.publicTeaserOnly) {
      q = query(q, where('publicPreview', '==', true))
    }
    
    // Filtre par compositeur
    if (filters.composer) {
      q = query(q, where('metadata.composer', '==', filters.composer))
    }
    
    // Filtre par difficulté (facile/difficile incluent anciennes valeurs débutant/avancé)
    if (filters.difficulty) {
      const values = getDifficultyFilterValues(filters.difficulty)
      if (values.length === 1) {
        q = query(q, where('metadata.difficulty', '==', values[0]))
      } else {
        q = query(q, where('metadata.difficulty', 'in', values))
      }
    }
    
    // Filtre par tags (autoTags contient un tag spécifique)
    // Note: Firestore ne supporte pas les requêtes "array-contains-any" directement
    // On va récupérer tous les exercices publics et filtrer côté client
    const querySnapshot = await getDocs(q)
    let exercises = []
    
    querySnapshot.forEach((doc) => {
      exercises.push(cleanExerciseTags({
        id: doc.id,
        ...doc.data()
      }))
    })

    // Nouveaux Horizons : ne filtrer par section que lorsqu'on demande explicitement les horizons
    // Sinon on renvoie tous les exercices publiés (y compris classiques sans section, pour éviter que des exercices publiés n'apparaissent nulle part)
    if (filters.onlyHorizons) {
      exercises = exercises.filter(ex => ex.metadata?.section === 'horizons')
    }

    // Filtrer par musicCategory si onlyHorizons et qu'un style est demandé
    if (filters.onlyHorizons && filters.musicCategory) {
      exercises = exercises.filter(ex => ex.metadata?.musicCategory === filters.musicCategory)
    }

    // Exclure les exercices Nouveaux Horizons (utilisé par défaut dans les vues classiques)
    if (filters.excludeHorizons) {
      exercises = exercises.filter(ex => ex.metadata?.section !== 'horizons')
    }

    // Filtrer par tags côté client si nécessaire
    if (filters.tags && filters.tags.length > 0) {
      return exercises.filter(exercise => {
        const exerciseTags = exercise.autoTags || []
        return filters.tags.some(tag => exerciseTags.includes(tag))
      })
    }
    
    // Filtrer par type d'accord (ex: "Contient des 6tes Augmentées")
    if (filters.chordType) {
      return exercises.filter(exercise => {
        const exerciseTags = exercise.autoTags || []
        return exerciseTags.some(tag => 
          tag.toLowerCase().includes(filters.chordType.toLowerCase())
        )
      })
    }
    
    // Recherche textuelle (filtrage côté client)
    // Recherche dans workTitle, exerciseTitle, et composer
    if (filters.searchText && filters.searchText.trim()) {
      const searchLower = filters.searchText.toLowerCase().trim()
      return exercises.filter(exercise => {
        const workTitle = exercise.metadata?.workTitle || ''
        const exerciseTitle = exercise.metadata?.exerciseTitle || ''
        const composer = getComposerDisplayName(exercise.metadata) || ''
        return (
          workTitle.toLowerCase().includes(searchLower) ||
          exerciseTitle.toLowerCase().includes(searchLower) ||
          composer.toLowerCase().includes(searchLower)
        )
      })
    }
    
    // Filtre par genre (metadata.genre ou mot-clé dans workTitle)
    if (filters.genre) {
      const genreLower = filters.genre.toLowerCase()
      exercises = exercises.filter(exercise => {
        const metaGenre = exercise.metadata?.genre
        if (metaGenre && metaGenre.toLowerCase() === genreLower) return true
        const workTitle = (exercise.metadata?.workTitle || '').toLowerCase()
        return workTitle.includes(genreLower)
      })
    }

    // Filtre par formation (metadata.formation = array) : l'exercice doit contenir toutes les formations sélectionnées
    if (filters.formation && (Array.isArray(filters.formation) ? filters.formation.length > 0 : filters.formation)) {
      const wanted = Array.isArray(filters.formation) ? filters.formation : [filters.formation]
      exercises = exercises.filter(exercise => {
        const exFormation = exercise.metadata?.formation
        const exArr = Array.isArray(exFormation) ? exFormation : (exFormation ? [exFormation] : [])
        return wanted.every(f => exArr.includes(f))
      })
    }
    
    return exercises
  } catch (error) {
    console.error('Erreur lors de la recherche d\'exercices:', error)
    throw error
  }
}

/**
 * Récupère les derniers exercices publics ajoutés (tri par createdAt décroissant)
 */
export async function getLatestPublicExercises(limitCount = 10, options = {}) {
  const safeLimit = Number(limitCount) > 0 ? Number(limitCount) : 10
  const publicTeaserOnly = !!options.publicTeaserOnly
  try {
    let q = query(
      collection(db, 'exercises'),
      where('status', '==', 'published'),
      orderBy('createdAt', 'desc'),
      limit(Math.max(20, safeLimit * 4))
    )
    if (publicTeaserOnly) {
      q = query(q, where('publicPreview', '==', true))
    }
    const snapshot = await getDocs(q)
    const exercises = snapshot.docs
      .map((docSnap) => cleanExerciseTags({
      id: docSnap.id,
      ...docSnap.data()
      }))
      .filter((ex) => ex.metadata?.section !== 'horizons')
      .slice(0, safeLimit)
    return exercises
  } catch (error) {
    console.error('Erreur lors de la récupération des derniers exercices:', error)
    // Fallback robuste : conserve l'ancien comportement si index/requête indisponible.
    const exercises = await searchPublicExercises({
      excludeHorizons: true,
      ...(publicTeaserOnly ? { publicTeaserOnly: true } : {}),
    })
    const sorted = [...exercises].sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(0)
      const dateB = b.createdAt?.toDate?.() || new Date(0)
      return dateB - dateA
    })
    return sorted.slice(0, safeLimit)
  }
}

/**
 * Récupère un exercice aléatoire parmi les exercices publics.
 * Si doneExerciseIds est fourni, priorise les exercices non faits ; on ne tire parmi les déjà faits que si tout est fait.
 * @param {Object} filters - Filtres searchPublicExercises (onlyHorizons, musicCategory, etc.)
 * @param {string[]} [filters.doneExerciseIds] - IDs déjà faits : on tire en priorité parmi les autres
 */
export async function getRandomPublicExercise(filters = {}) {
  try {
    const { doneExerciseIds, publicTeaserOnly, ...restFilters } = filters
    const effectiveFilters = restFilters.onlyHorizons ? restFilters : { ...restFilters, excludeHorizons: true }
    const exercises = await searchPublicExercises({
      ...effectiveFilters,
      ...(publicTeaserOnly ? { publicTeaserOnly: true } : {}),
    })

    if (exercises.length === 0) {
      return null
    }

    const pool = (Array.isArray(doneExerciseIds) && doneExerciseIds.length > 0)
      ? exercises.filter(ex => !doneExerciseIds.includes(ex.id))
      : []
    const finalPool = pool.length > 0 ? pool : exercises
    const randomIndex = Math.floor(Math.random() * finalPool.length)
    return finalPool[randomIndex]
  } catch (error) {
    console.error('Erreur lors de la récupération d\'un exercice aléatoire:', error)
    throw error
  }
}

/**
 * Crée un nouvel exercice
 */
export async function createExercise(exerciseData) {
  try {
    const sanitized = sanitizeForFirestore(exerciseData)
    const docRef = await addDoc(collection(db, 'exercises'), {
      ...sanitized,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
    return docRef.id
  } catch (error) {
    console.error('Erreur lors de la création de l\'exercice:', error)
    throw error
  }
}

/**
 * Met à jour un exercice existant
 */
export async function updateExercise(exerciseId, exerciseData) {
  try {
    const docRef = doc(db, 'exercises', exerciseId)
    const sanitized = sanitizeForFirestore(exerciseData)
    await updateDoc(docRef, {
      ...sanitized,
      updatedAt: serverTimestamp()
    })
    return exerciseId
  } catch (error) {
    console.error('Erreur lors de la mise à jour de l\'exercice:', error)
    throw error
  }
}

/** Vitrine beta : visible sans compte autorisé quand la beta Harmonist est active (admin uniquement côté rules). */
export async function setExercisePublicPreview(exerciseId, publicPreview) {
  return updateExercise(exerciseId, { publicPreview: !!publicPreview })
}

/**
 * Supprime un exercice
 */
export async function deleteExercise(exerciseId) {
  try {
    const docRef = doc(db, 'exercises', exerciseId)
    await deleteDoc(docRef)
  } catch (error) {
    console.error('Erreur lors de la suppression de l\'exercice:', error)
    throw error
  }
}

/**
 * Duplique un exercice (crée une copie)
 */
export async function duplicateExercise(exerciseId, newAuthorId, newAuthorName) {
  try {
    const originalExercise = await getExerciseById(exerciseId)
    if (!originalExercise) {
      throw new Error('Exercice introuvable')
    }
    
    // Créer une copie sans l'ID et les timestamps
    const { id, createdAt, updatedAt, ...exerciseData } = originalExercise
    
    const newExercise = {
      ...exerciseData,
      authorId: newAuthorId,
      authorName: newAuthorName,
      publicPreview: false,
      metadata: {
        ...exerciseData.metadata,
        title: `${exerciseData.metadata.title} (Copie)`
      },
      status: 'draft'
    }
    
    return await createExercise(newExercise)
  } catch (error) {
    console.error('Erreur lors de la duplication de l\'exercice:', error)
    throw error
  }
}

/**
 * Recalcule les autoTags d'un exercice à partir de ses markers et metadata (même logique que SaveExerciseModal).
 * Utile après un changement de la logique de génération des tags.
 * @param {Object} exercise - Exercice avec markers (array de { time, chord }), metadata (composer, section, etc.)
 * @returns {string[]} - Nouveaux autoTags sanitized
 */
export function computeAutoTagsForStoredExercise(exercise) {
  return computeAutoTagsForStoredExerciseImpl(exercise)
}

/**
 * Met à jour les autoTags de tous les exercices d'un auteur (recalcul avec la logique actuelle).
 * @param {string} authorId - UID de l'auteur
 * @returns {Promise<{ updated: number, failed: number, errors: Array<{ id: string, message: string }> }>}
 */
export async function refreshAllAutoTagsForAuthor(authorId) {
  const result = { updated: 0, failed: 0, errors: [] }
  if (!authorId) return result
  const exercises = await getExercisesByAuthor(authorId)
  for (const ex of exercises) {
    try {
      const autoTags = computeAutoTagsForStoredExercise(ex)
      await updateExercise(ex.id, { autoTags })
      result.updated++
    } catch (err) {
      result.failed++
      result.errors.push({ id: ex.id, message: err.message || String(err) })
    }
  }
  return result
}

/**
 * Met à jour les autoTags de TOUS les exercices (réservé aux profs pouvant éditer tous les exercices).
 * @returns {Promise<{ updated: number, failed: number, errors: Array<{ id: string, message: string }> }>}
 */
export async function refreshAllAutoTags() {
  const result = { updated: 0, failed: 0, errors: [] }
  const exercises = await getAllExercisesForTeachers()
  for (const ex of exercises) {
    try {
      const autoTags = computeAutoTagsForStoredExercise(ex)
      await updateExercise(ex.id, { autoTags })
      result.updated++
    } catch (err) {
      result.failed++
      result.errors.push({ id: ex.id, message: err.message || String(err) })
    }
  }
  return result
}

/**
 * Recherche les exercices utilisant la même vidéo YouTube.
 * Utilise deux requêtes (publiés + les miens) pour respecter les règles Firestore :
 * la règle read n'autorise que published ou authorId == uid, donc une seule requête
 * par video.id échoue s'il existe un brouillon d'un autre auteur.
 */
export async function findExercisesByVideoId(videoId) {
  try {
    const byId = new Map()
    const add = (snap) => {
      snap.forEach((d) => {
        if (!byId.has(d.id)) byId.set(d.id, cleanExerciseTags({ id: d.id, ...d.data() }))
      })
    }
    const qPublished = query(
      collection(db, 'exercises'),
      where('video.id', '==', videoId),
      where('status', '==', 'published')
    )
    const snapPublished = await getDocs(qPublished)
    add(snapPublished)
    let mineCount = 0
    const uid = auth.currentUser?.uid
    if (uid) {
      const qMine = query(
        collection(db, 'exercises'),
        where('video.id', '==', videoId),
        where('authorId', '==', uid)
      )
      const snapMine = await getDocs(qMine)
      mineCount = snapMine.size
      add(snapMine)
    }
    const exercises = Array.from(byId.values())
    return exercises
  } catch (error) {
    console.error('Erreur lors de la recherche d\'exercices par vidéo:', error)
    throw error
  }
}

/**
 * Trie les exercices par date de création puis par id (ordre stable pour le rang).
 */
function sortExercisesByCreatedAtId(exercises) {
  return [...exercises].sort((a, b) => {
    const tA = a.createdAt?.toDate?.()?.getTime() ?? a.createdAt ?? 0
    const tB = b.createdAt?.toDate?.()?.getTime() ?? b.createdAt ?? 0
    if (tA !== tB) return tA - tB
    return (a.id || '').localeCompare(b.id || '')
  })
}

/**
 * Pour une liste d'exercices, calcule le rang (1/X) de chaque exercice parmi TOUS les exercices
 * créés et publiés (même vidéo) dans Opus Lab. Utilisé pour afficher le badge 1/X (uniquement si X >= 2).
 * @param {Array<{ id: string, video?: { id: string } }>} exercises - exercices avec au moins id et video.id
 * @returns {Promise<Map<string, { rank: number, groupSize: number }>>} map exerciseId -> { rank, groupSize }
 */
export async function getVideoRanksForExercises(exercises) {
  const rankMap = new Map()
  if (!exercises || exercises.length === 0) return rankMap
  const videoIds = [...new Set(exercises.map((e) => e.video?.id).filter(Boolean))]
  for (const videoId of videoIds) {
    const list = await findExercisesByVideoId(videoId)
    const sorted = sortExercisesByCreatedAtId(list)
    const groupSize = sorted.length
    sorted.forEach((ex, index) => {
      rankMap.set(ex.id, { rank: index + 1, groupSize })
    })
  }
  return rankMap
}

/**
 * Même chose que getVideoRanksForExercises mais à partir d'une liste d'IDs d'exercices.
 * Charge chaque exercice pour obtenir son video.id, puis calcule les rangs.
 * @param {string[]} exerciseIds
 * @returns {Promise<Map<string, { rank: number, groupSize: number }>>}
 */
export async function getVideoRanksForExerciseIds(exerciseIds) {
  if (!exerciseIds || exerciseIds.length === 0) return new Map()
  const uniq = [...new Set(exerciseIds.filter(Boolean))]
  const exercises = (await Promise.all(uniq.map((id) => getExerciseById(id)))).filter(Boolean)
  return getVideoRanksForExercises(exercises)
}

const PARCOURS_LAST_MODE_KEY = 'parcoursLastMode'
const PARCOURS_LAST_EXERCISE_KEY_PREFIX = 'parcoursLastExercise:'

export function getParcoursLastExerciseId(nodeId) {
  if (!nodeId || typeof sessionStorage === 'undefined') return null
  return sessionStorage.getItem(`${PARCOURS_LAST_EXERCISE_KEY_PREFIX}${nodeId}`)
}

export function setParcoursLastExerciseId(nodeId, exerciseId) {
  if (!nodeId || !exerciseId || typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(`${PARCOURS_LAST_EXERCISE_KEY_PREFIX}${nodeId}`, exerciseId)
}

/**
 * Détermine le mode dominant d'un exercice (majeur / mineur) pour le biais anti-alternance.
 * Utilise autoTags (Majeur / Mineur) si présents, sinon compte des degreeMode des accords.
 * @param {Object} exercise - Exercice avec autoTags et/ou markers[].chord.degreeMode
 * @returns {'major'|'minor'|null} - null = mixte ou indéterminé
 */
export function getExerciseDominantMode(exercise) {
  if (!exercise) return null
  const tags = exercise.autoTags || []
  if (tags.includes('Majeur')) return 'major'
  if (tags.includes('Mineur')) return 'minor'
  if (!exercise.markers || !exercise.markers.length) return null
  let major = 0
  let minor = 0
  for (const marker of exercise.markers) {
    const chord = typeof marker === 'object' && marker.chord ? marker.chord : null
    if (!chord?.degreeMode) continue
    if (chord.degreeMode === 'major' || chord.degreeMode === 'ionian') major++
    else if (chord.degreeMode === 'minor' || chord.degreeMode === 'aeolian') minor++
  }
  const total = major + minor
  if (total === 0) return null
  if (major > minor) return 'major'
  if (minor > major) return 'minor'
  return null
}

/**
 * Récupère un exercice adapté pour un nœud de progression
 * Option A : biais anti-alternance majeur/mineur (55 % vers l'opposé du dernier mode).
 * Plafond de difficulté par étage (strict, sans élargissement hors plafond).
 * @param {string} nodeId - ID du nœud parcours
 * @param {{ lastMode?: 'major'|'minor'|null, doneExerciseIds?: string[], preferredDifficulty?: string, excludeExerciseId?: string|null }} [options]
 */
export async function getExercisesForNode(nodeId, options = {}) {
  try {
    const { lastMode, doneExerciseIds, preferredDifficulty, excludeExerciseId } = options
    const allExercises = await searchPublicExercises({ excludeHorizons: true })

    const suitableExercises = allExercises.filter(exercise => {
      return isExerciseSuitableForNode(exercise, nodeId, 2)
    })

    if (suitableExercises.length === 0) {
      console.warn(`Aucun exercice trouvé pour le nœud ${nodeId}`)
      return null
    }

    const allowed = getAllowedDifficultiesForNode(nodeId)
    const preferred = preferredDifficulty || allowed[0] || 'facile'

    const poolByDifficulty = pickParcoursRandomPool(suitableExercises, {
      allowed,
      preferred,
      doneExerciseIds,
      excludeExerciseId,
    })

    if (!poolByDifficulty?.length) {
      console.warn(
        `Aucun exercice dans le plafond de difficulté (${allowed.join(', ')}) pour le nœud ${nodeId}`
      )
      return null
    }

    if (poolByDifficulty.length === 1 || !lastMode) {
      return poolByDifficulty[Math.floor(Math.random() * poolByDifficulty.length)]
    }
    const majorList = []
    const minorList = []
    const otherList = []
    for (const ex of poolByDifficulty) {
      const mode = getExerciseDominantMode(ex)
      if (mode === 'major') majorList.push(ex)
      else if (mode === 'minor') minorList.push(ex)
      else otherList.push(ex)
    }
    const preferOpposite = lastMode === 'major' ? 'minor' : 'major'
    const oppositeList = preferOpposite === 'major' ? majorList : minorList
    const sameList = preferOpposite === 'minor' ? majorList : minorList
    const poolOpposite = oppositeList.length > 0 ? oppositeList : otherList
    const poolSame = sameList.length > 0 ? sameList : otherList
    const useOpposite = Math.random() < 0.55
    const list = useOpposite ? poolOpposite : poolSame
    const fallback = list === poolOpposite ? poolSame : poolOpposite
    const final = list.length > 0 ? list : fallback
    return final[Math.floor(Math.random() * final.length)]
  } catch (error) {
    console.error('Erreur lors de la récupération d\'un exercice pour le nœud:', error)
    return null
  }
}

/**
 * Récupère la liste des exercices adaptés à un nœud de progression.
 * Plafond d'étage strict : uniquement les exercices dont la difficulté est autorisée.
 * @param {string} nodeId - ID du nœud parcours
 * @returns {Promise<Array>} - Liste des exercices (métadonnées) adaptés au nœud
 */
export async function getExercisesListForNode(nodeId) {
  try {
    const allExercises = await searchPublicExercises({ excludeHorizons: true })
    const suitable = allExercises.filter(exercise => isExerciseSuitableForNode(exercise, nodeId, 2))
    if (suitable.length === 0) return []

    const allowed = new Set(getAllowedDifficultiesForNode(nodeId))
    const withinCeiling = suitable.filter(ex =>
      allowed.has(getDisplayDifficulty(ex.metadata?.difficulty))
    )
    return withinCeiling
  } catch (error) {
    console.error('Erreur lors de la récupération de la liste d\'exercices pour le nœud:', error)
    return []
  }
}

/** Clé sessionStorage pour le dernier mode (parcours). Export pour usage dans CampaignMap. */
export { PARCOURS_LAST_MODE_KEY, PARCOURS_LAST_EXERCISE_KEY_PREFIX }

